import { Fragment } from 'react';
import { makeStyles } from '@mui/styles';
import { Grid, Typography } from '@ts/storybook';
import OlFeature from 'ol/Feature.js';
import { CMapElement } from '@models/maps/Map';
import { CReading } from '@ts/ui-models';
import { StatusCell } from '@ts/storybook';
import { TRecordItems, TRecordLocation } from '@interfaces/maps/Map.interface';
import { CLocation } from '@ts/ui-models';

const useStyles = makeStyles({
  tooltipMap: {
    maxHeight: '400px',
    overflow: 'auto',
    backgroundColor: 'rgba(255, 255, 255, 1)',
    '-webkit-filter': 'drop-shadow(0 1px 4px rgba(0, 0, 0, 0.2))',
    filter: ' drop-shadow(0 1px 4px rgba(0, 0, 0, 0.2))',
    padding: ' 10px 10px 10px 10px',
    borderRadius: ' 10px',
    margin: ' 10px',
    border: ' 1px solid #000',
    minWidth: ' 240px',
    color: ' #000',
  },
  /* tooltipInfoSideBar: {
    maxHeight: '100%',
    overflow: 'auto',
    backgroundColor: 'white',
    color: ' black',
  }, */
  divider: {
    height: ' 1px',
    width: ' 100%',
    background: ' black',
    marginTop: ' 5px',
    marginBottom: ' 5px',
  },
  dividerData: {
    borderTop: '1px dashed black',
    width: '100%',
    marginTop: '5px',
    marginBottom: '5px',
  },
  smallTooltip: {
    fontSize: 22,
  },
});

interface TTooltipMapProps {
  features: OlFeature[];
  itemsMap: TRecordItems;
  locations: TRecordLocation | null;
}

const TTooltipMap = (props: TTooltipMapProps) => {
  const classesStyle = useStyles();
  // console.log('props.features', props.features);

  const { itemsMap, locations, features } = props;
  if (features.length === 0) return;
  const isCluster = !features.some(feature => !feature.get('itemID')) && features.length > 1;

  //#region Functions
  /**
   *  Counts the occurrences of each item type in the given list of items.
   *
   *  @param {any[]} items - The list of items to count types from.
   *  @return {any[]} An array of objects containing the type and its corresponding count.
   */
  const countTypesInClusters = (items: OlFeature[]) => {
    const count: { [key: string]: number } = {};

    items.forEach(item => {
      const tItemMap = itemsMap[item.get('itemID')];
      if (tItemMap) {
        if (tItemMap.elementType) {
          const value: string = tItemMap.elementType.name;
          if (value in count) {
            count[value]++;
          } else {
            count[value] = 1;
          }
        }
      }
    });

    const result: any = [];
    for (const type in count) {
      result.push({ type, count: count[type] });
    }

    return result;
  };
  //#endregion

  //#region Render
  const renderDivider = (type: 'item' | 'data' = 'item') => {
    return <div className={type === 'data' ? classesStyle.dividerData : classesStyle.divider} />;
  };

  const renderLocation = (feature: OlFeature) => {
    const location = feature.get('tLocation') as CLocation;
    if (!location) return null;
    return (
      <>
        <Typography
          variant="body2"
          text={[<b>Location</b>, ': ', location.getName(), ' (', location.getVisualizationID(), ')']}
        />
      </>
    );
  };

  const renderElement = (feature: OlFeature) => {
    const item = itemsMap[feature.get('itemID')];
    const location = locations?.[feature.get('entityID')];
    if (!item && !location) return null;
    const reading: CReading = feature.get('reading');
    const isLast = feature.get('isLast');
    if (item instanceof CMapElement)
      return (
        <>
          <Typography
            variant="body2"
            text={[<b>Name</b>, ' ', <b>(ID)</b>, ': ', item.getName(), ' (', item.getVisualizationID(), ')']}
          />
          {item.elementType && <Typography variant="body2" text={[<b>Item type</b>, ': ', item.elementType.getName()]} />}
          {reading && renderDivider('data')}
          {reading && <Typography variant="body2" text={[<b>Last update</b>, ': ', reading.getTimestamp]} />}
          {reading && <Typography variant="body2" text={[<b>{reading.getLabel}</b>, ': ', reading.getValueUnits]} />}
          {/* {isLast && <StatusCell indicators={item.statuses} isInMap />} */}
          {isLast && <StatusCell indicators={item.statuses?.filter(i => i.readingType && i.update?.live)} isInMap />}
        </>
      );

    if (location instanceof CLocation)
      return (
        <>
          <Typography
            variant="body2"
            text={[<b>Name</b>, ' ', <b>(ID)</b>, ': ', location.getName(), ' (', location.getVisualizationID(), ')']}
          />
          <Typography variant="body2" text={[<b>Item type</b>, ': ', location.elementType.name]} />
        </>
      );
  };

  const renderBubbleTooltip = () => {
    let itemRendered = false;

    return features.map((feature, index) => {
      let Content: any = null;

      if (feature.get('itemID')) {
        if (!itemRendered) {
          Content = renderElement(feature);
          itemRendered = true;
        }
      } else {
        Content = renderLocation(feature);
      }

      return (
        <Fragment key={feature.getId()}>
          {Content}
          {Content && features.length - 1 !== index ? renderDivider() : null}
        </Fragment>
      );
    });
  };

  const renderBubbleCluster = () => {
    const listTypes = countTypesInClusters(features);

    return listTypes.map((type: any, index) => {
      return (
        <Fragment key={index}>
          <Typography variant="body2" text={[<b>{type.count}</b>, ' ', 'items of type', ' ', <b>{type.type}</b>]} />
          {listTypes.length - 1 !== index ? renderDivider() : null}
        </Fragment>
      );
    });
  };

  //#endregion

  return <Grid className={classesStyle.tooltipMap}>{isCluster ? renderBubbleCluster() : renderBubbleTooltip()}</Grid>;
};

export default TTooltipMap;
